import React, { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, Box, CircularProgress, InputBase, Tab, Tabs, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import _debounce from "lodash/debounce";
import SearchIcon from "@mui/icons-material/Search";
import sh from "bundles/common/utils/sh";

const formatRelativeTime = (dateString, locale) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  const units = [
    ["year", 60 * 60 * 24 * 365],
    ["month", 60 * 60 * 24 * 30],
    ["week", 60 * 60 * 24 * 7],
    ["day", 60 * 60 * 24],
    ["hour", 60 * 60],
    ["minute", 60],
  ];

  for (const [unit, seconds] of units) {
    if (Math.abs(diffSeconds) >= seconds || unit === "minute") {
      return rtf.format(Math.round(diffSeconds / seconds), unit);
    }
  }

  return "";
};

// Perspective-agnostic conversation list shared by recruiters and candidates.
// `peerOf(conversation)` returns { name, avatar, subtitle } for the other party,
// and `selfType` ("Recruiter" | "Candidate") drives the "You:" prefix.
const ConversationList = ({
  conversations,
  selectedId,
  onSelect,
  searchEndpoint,
  peerOf,
  selfType,
  view = "active",
  onViewChange,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const requestIdRef = useRef(0);

  const debouncedSetSearch = useCallback(
    _debounce((value) => setDebouncedSearch(value), 400),
    [setDebouncedSearch]
  );

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    debouncedSetSearch(event.target.value);
  };

  useEffect(() => {
    const query = debouncedSearch.trim();
    if (!query) {
      setResults(null);
      setSearching(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setSearching(true);

    sh.get(searchEndpoint, { params: { query } })
      .then((res) => {
        if (requestIdRef.current !== requestId) return;
        setResults(res.data?.data || []);
      })
      .catch((err) => {
        if (requestIdRef.current !== requestId) return;
        console.error(err);
        setResults([]);
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setSearching(false);
      });
  }, [debouncedSearch, searchEndpoint]);

  const trimmedSearch = search.trim();
  const isSearching = trimmedSearch.length > 0;
  const hasResults = results !== null;
  const resultsReady = !searching && hasResults && debouncedSearch.trim() === trimmedSearch;

  const filteredConversations = isSearching && hasResults ? results : conversations;
  const showNoResults = isSearching && resultsReady && results.length === 0;

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "300px" },
        flexShrink: 0,
        bgcolor: theme.palette.common.white,
        border: "1px solid hsla(160, 10%, 90%, 0.6)",
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {onViewChange && (
        <Tabs
          value={view}
          onChange={(_event, value) => onViewChange(value)}
          variant="fullWidth"
          sx={{
            minHeight: 0,
            borderBottom: "1px solid hsl(160, 10%, 92%)",
            "& .MuiTab-root": { minHeight: 0, py: 1.25, textTransform: "none", fontSize: "13px" },
            "& .Mui-selected": { color: "hsl(153, 68%, 25%) !important" },
            "& .MuiTabs-indicator": { backgroundColor: "hsl(153, 68%, 25%)" },
          }}
        >
          <Tab value="active" label={intl.formatMessage({ id: "messaging.active_tab" })} />
          <Tab value="archived" label={intl.formatMessage({ id: "messaging.archived_tab" })} />
        </Tabs>
      )}

      <Box sx={{ p: 1.5, borderBottom: "1px solid hsl(160, 10%, 92%)" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            bgcolor: theme.palette.common.white,
            border: "1px solid hsl(160, 10%, 90%)",
            borderRadius: "12px",
            "&:focus-within": {
              borderColor: "hsl(153, 68%, 25%)",
              boxShadow: "0 0 0 3px hsla(153, 68%, 25%, 0.15)",
            },
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
          <InputBase
            fullWidth
            value={search}
            onChange={handleSearchChange}
            placeholder={intl.formatMessage({ id: "messaging.search_placeholder" })}
            sx={{ fontSize: "14px", color: theme.palette.text.main }}
          />
          {searching && <CircularProgress size={16} sx={{ color: theme.palette.text.secondary }} />}
        </Box>
      </Box>

      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {showNoResults && (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {intl.formatMessage({ id: "messaging.search_no_results" })}
            </Typography>
          </Box>
        )}
        {filteredConversations.map((conversation) => {
          const isSelected = conversation.id === selectedId;
          const isBlocked = conversation.blocked;
          const peer = peerOf(conversation);
          const lastMessage = conversation.last_message;
          const preview = lastMessage
            ? `${lastMessage.sender_type === selfType ? `${intl.formatMessage({ id: "messaging.you_prefix" })} ` : ""}${lastMessage.body}`
            : "";

          const restingBg = isBlocked
            ? "hsla(0, 70%, 50%, 0.06)"
            : isSelected
              ? alpha(theme.palette.primary.main, 0.06)
              : theme.palette.common.white;

          return (
            <Box
              key={conversation.id}
              onClick={() => onSelect(conversation.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.5,
                cursor: "pointer",
                borderBottom: "1px solid hsl(160, 10%, 94%)",
                bgcolor: restingBg,
                transition: "all 0.3s",
                "&:hover": {
                  bgcolor: isBlocked ? "hsla(0, 70%, 50%, 0.1)" : isSelected
                    ? alpha(theme.palette.primary.main, 0.06)
                    : "hsl(160, 10%, 97%)",
                },
              }}
            >
              <Avatar
                src={peer?.avatar || undefined}
                alt={peer?.name}
                sx={{ width: 44, height: 44 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography
                    variant="h7"
                    noWrap
                    sx={{ color: theme.palette.text.main, fontWeight: conversation.unread ? 700 : 400 }}
                  >
                    {peer?.name}
                  </Typography>
                  <Typography variant="body3" sx={{ color: theme.palette.text.secondary, flexShrink: 0 }}>
                    {formatRelativeTime(conversation.last_message_at, i18nLocale)}
                  </Typography>
                </Box>
                {peer?.subtitle && (
                  <Typography variant="body3" noWrap sx={{ color: theme.palette.text.light_grey, display: "block" }}>
                    {peer.subtitle}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isBlocked && (
                    <Box
                      component="span"
                      sx={{
                        px: 0.75,
                        py: 0.1,
                        fontSize: "10px",
                        fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                        color: theme.palette.error.main,
                        bgcolor: "hsla(0, 70%, 50%, 0.12)",
                        borderRadius: "9999px",
                        flexShrink: 0,
                      }}
                    >
                      {intl.formatMessage({ id: "messaging.blocked_label" })}
                    </Box>
                  )}
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      color: conversation.unread ? theme.palette.text.main : theme.palette.text.secondary,
                      fontWeight: conversation.unread ? 600 : 300,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {preview}
                  </Typography>
                  {conversation.unread && (
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "9999px",
                        bgcolor: "#D9F752",
                        border: "1px solid rgba(2, 51, 49, 0.35)",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ConversationList;
